import { useEffect, useState } from 'react';
import {
  ItemBomType,
  productionProgressUpdatePostApi,
  ProductionProgressUpdateReqType,
  ProductionResponseType,
  productionStockHistoryFindApi,
  ProductionStockHistoryRequestType,
  productionUnsyncedStockHistoryFindApi,
  StockHistoryResponseType,
  UnsyncedStockHistoryResponseType,
  workbayListGetApi,
  WorkbayType,
} from '../../../api/apiConfig';
import useFetch from '../../../api/useFetch';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/modules';
import { CustomCircularProgressbar } from '../../../component/common/CustomCircularProgressbar';
import { ProductionSquareCard } from '../../../component/common/ProductionSquareCard';
import { ProductionCircularCard } from '../../../component/common/ProductionCircularCard';
import { useHeaderButtons } from '../../../component/header/HeaderButtonProvider';
import SquareButton from '../../../component/button/SquareButton';
import { useDispatch } from 'react-redux';
import { toggleSidebar } from '../../../store/modules/sidebar';
import { setHeaderOpenstate } from '../../../store/modules/header';
import { useNavigate } from 'react-router-dom';
import { CustomProgressBar } from '../../../component/common/CustomProgressBar';
import {
  convertNumberStringToNumber,
  formatEightDigitStringToSixDigitDot,
  getYearMonthDateFromDate,
} from '../../../function/replaceString';
import { createExcelFileWithColumnLetters } from '../../../function/createExcel';
import { setUnsyncedStockHistoryData } from '../../../store/modules/socketData';

type StockHistoryResponseEssType = {
  itemId: number;
  prodDes: string;
  sizeDes: string;
  changedQuantities: number;
  prodCd: string;
};

type StockHistoryEssType = {
  itemId: number;
  prodDes: string;
  sizeDes: string;
  expectedChangedQty: number;
  changedQty: number | string;
  prodCd: string;
};

type ProductionInboundDataType = {
  productionDt: string;
  orders: number;
  factoryCd: string;
  empCd: string;
  receiveWarehouseCd: string;
  producedCd: string;
  producedDes: string;
  producedQty: string;
  consumedCd: string;
  consumedDes: string;
  consumedQty: string;
  groupNo: number;
};

export const ProductionInbound = () => {
  const [nowDateString, setNowDateString] = useState<string>(
    getYearMonthDateFromDate(new Date()).dateString
  );

  const loginUserName = useSelector((state: RootState) => state.loginUserData?.userName);


  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { fetch: fetchProductionProgressUpdate } = useFetch({
    fetchFunction: productionProgressUpdatePostApi,
  });

  const { fetch: fetchProductionStockHistoryFind } = useFetch({
    fetchFunction: productionStockHistoryFindApi,
    onSuccess: (data: StockHistoryResponseEssType[]) => {
      initStockHistoryDatas(data);
      setStockHistoriesOrigin(data);
    },
  });

  const { fetch: fetchProductionUnsyncedStockHistoryFindApi } = useFetch({
    fetchFunction: productionUnsyncedStockHistoryFindApi,
    onSuccess: (data: StockHistoryResponseEssType[]) => {
      initUnsyncedStockHistoryDatas(data);
    },
  });

  function initStockHistoryDatas(data: StockHistoryResponseEssType[]) {
    // Step 1: 변환된 데이터 초기화
    const transformedData = data.map((response) => ({
      itemId: response.itemId,
      prodDes: response.prodDes,
      sizeDes: response.sizeDes,
      expectedChangedQty: response.changedQuantities * -1,
      changedQty: response.changedQuantities * -1,
      prodCd: response.prodCd,
    }));

    // Step 2: itemId 기준으로 그룹화 및 합산
    const stockHistoryMap = new Map<number, StockHistoryEssType>();

    transformedData.forEach((stockHistory) => {
      if (stockHistoryMap.has(stockHistory.itemId)) {
        // 이미 존재하는 itemId는 값을 합산
        const existingHistory = stockHistoryMap.get(stockHistory.itemId)!;
        existingHistory.expectedChangedQty += stockHistory.expectedChangedQty;
        const existingQty = existingHistory.changedQty;
        const changedQty = stockHistory.changedQty;

        existingHistory.changedQty =
          convertNumberStringToNumber(existingQty) +
          convertNumberStringToNumber(changedQty);
      } else {
        // 새로운 itemId는 맵에 추가
        stockHistoryMap.set(stockHistory.itemId, { ...stockHistory });
      }
    });

    // Step 3: 상태 업데이트
    setStockHistories(Array.from(stockHistoryMap.values()));
  }

  function initUnsyncedStockHistoryDatas(data: StockHistoryResponseEssType[]) {
    // Step 1: 변환된 데이터 초기화
    const transformedData = data.map((response) => ({
      itemId: response.itemId,
      prodDes: response.prodDes,
      sizeDes: response.sizeDes,
      expectedChangedQty: response.changedQuantities * -1,
      changedQty: response.changedQuantities * -1,
      prodCd: response.prodCd,
    }));

    // Step 2: itemId 기준으로 그룹화 및 합산
    const stockHistoryMap = new Map<number, StockHistoryEssType>();

    transformedData.forEach((stockHistory) => {
      if (stockHistoryMap.has(stockHistory.itemId)) {
        // 이미 존재하는 itemId는 값을 합산
        const existingHistory = stockHistoryMap.get(stockHistory.itemId)!;
        existingHistory.expectedChangedQty += stockHistory.expectedChangedQty;

        const existingQty = existingHistory.changedQty;
        const changedQty = stockHistory.changedQty;

        existingHistory.changedQty =
          convertNumberStringToNumber(existingQty) +
          convertNumberStringToNumber(changedQty);
      } else {
        // 새로운 itemId는 맵에 추가
        stockHistoryMap.set(stockHistory.itemId, { ...stockHistory });
      }
    });

    // Step 3: 상태 업데이트
    setFilteredUshList(Array.from(stockHistoryMap.values()));
  }

  const [stockHistories, setStockHistories] = useState<StockHistoryEssType[]>(
    []
  );

  const unsyncedStockHistories = useSelector(
    (state: RootState) => state.socketData.unsyncedStockHistory
  );

  const [filteredUshList, setFilteredUshList] = useState<StockHistoryEssType[]>(
    []
  );

  const [stockHistoriesOrigin, setStockHistoriesOrigin] = useState<
    StockHistoryResponseEssType[]
  >([]);

  const sidebarOpenStatus = useSelector(
    (state: RootState) => state.sidebar.uiStatus.open
  );

  const productionData = useSelector(
    (state: RootState) => state.socketData.production
  );

  // const stockHistories = useSelector(
  //   (state: RootState) => state.socketData.stockHistory
  // );

  const [filteredProductions, setFilteredProductions] = useState<
    ProductionResponseType[]
  >([]);

  const [unCompletedProductions, setUnCompletedProductions] = useState<
    ProductionResponseType[]
  >([]);

  const test = useSelector((state: RootState) => state.socketData.stockHistory);

  const headerOpenStatus = useSelector((state: RootState) => state.header.open);
  const { setButtons } = useHeaderButtons();
  const [fullScreen, setFullScreen] = useState<boolean>(false);

  const [workbayList, setWorkbayList] = useState<WorkbayType[]>([]);
  const [selectedWorkbayId, setSelectedWorkbayId] = useState<number>(-1);
  const { fetch: fetchWorkbayListGet } = useFetch({
    fetchFunction: workbayListGetApi,
    onSuccess: (data: WorkbayType[]) => setWorkbayList(data),
  });

  //생산시작버튼 활성화 트리거
  const [clickable, setClickable] = useState<boolean>(false);

  const [stockHistoryGroup, setStockHistoryGroup] = useState<
    StockHistoryResponseType[]
  >([]);

  useEffect(() => {
    fetchWorkbayListGet();
  }, []);

  useEffect(() => {
    if (selectedWorkbayId === -1) return;
    const filteredProductions: ProductionResponseType[] = productionData.filter(
      (production) => Number(selectedWorkbayId) === Number(production.workbayId)
    );
    setFilteredProductions(filteredProductions);
    // setFilteredUshList(
    //   unsyncedStockHistories.filter(
    //     (ush) => ush.workbayId === selectedWorkbayId
    //   )
    // );
    setUnCompletedProductions(
      productionData.filter(
        (production) =>
          Number(selectedWorkbayId) === Number(production.workbayId) &&
          production.progress !== 'E' &&
          production.progress !== 'C'
      )
    );
    fetchProductionUnsyncedStockHistoryFindApi({
      workbayId: selectedWorkbayId,
      timeDate: nowDateString,
    });
  }, [selectedWorkbayId]);

  function progressUpdate() {
    const targetItem = productionData.find(
      (production) => Number(production.workbayId) === Number(selectedWorkbayId)
    );
    if (!targetItem) return;
    const dto: ProductionProgressUpdateReqType = {
      progress: 'C',
      targetDate: targetItem.productionDt,
      workbayId: selectedWorkbayId,
    };
    fetchProductionProgressUpdate(dto);
  }

  useEffect(() => {
    if (filteredProductions.length < 1) {
      setStockHistories([]);
      setStockHistoriesOrigin([]);
      return;
    }
    if (selectedWorkbayId === -1) return;
    const requestDto: ProductionStockHistoryRequestType = {
      workbayId: selectedWorkbayId,
      timeDate: nowDateString,
    };
    fetchProductionStockHistoryFind(requestDto);
  }, [filteredProductions]);

  /** 엑셀 다운로드 핸들러 */
  function excelDownloadHandler(data: ProductionInboundDataType[]) {
    // 열 알파벳과 데이터 키 매핑
    const columns = [
      {
        header: '일자',
        key: 'productionDt',
        columnLetter: 'A',
      },
      {
        header: '순번',
        key: 'orders',
        columnLetter: 'B',
      },
      {
        header: '담당자',
        key: 'empCd',
        columnLetter: 'C',
      },
      {
        header: '생산된공장',
        key: 'factoryCd',
        columnLetter: 'D',
      },
      {
        header: '받는창고',
        key: 'receiveWarehouseCd',
        columnLetter: 'E',
      },
      {
        header: '생산품목코드',
        key: 'producedCd',
        columnLetter: 'F',
      },
      {
        header: '묶음번호',
        key: 'groupNo',
        columnLetter: 'G',
      },
      {
        header: '생산수량',
        key: 'producedQty',
        columnLetter: 'H',
      },
      {
        header: '소모품목코드(소모)',
        key: 'consumedCd',
        columnLetter: 'I',
      },
      {
        header: '소모품목명(소모)',
        key: 'consumedDes',
        columnLetter: 'J',
      },
      {
        header: '소모수량(소모)',
        key: 'consumedQty',
        columnLetter: 'K',
      },
    ];
    createExcelFileWithColumnLetters(data, columns, 'excel-convert-test');
  }

  function translateToExcel(
    stockHistories: StockHistoryEssType[],
    productionDatas: ProductionResponseType[],
    unsyncedStockHistories: StockHistoryEssType[]
  ) {
    const combinedProductionDatas: ProductionResponseType[] = [];
    productionDatas
      .filter((production) => production.productionCnt > 0)
      .forEach((data, index) => {
        const existingProductionItem = combinedProductionDatas.find(
          (productionData) => productionData.item.id === data.item.id
        );
        if (existingProductionItem) {
          existingProductionItem.productionCnt += data.productionCnt;
        } else {
          combinedProductionDatas.push(data);
        }
      });

    const inboundArray: ProductionInboundDataType[] = [];

    const stockHistoriesWithoutProduced = stockHistories.filter(
      (stockHistory) =>
        !combinedProductionDatas.some(
          (production) => production.item.PROD_CD === stockHistory.prodCd
        )
    );

    stockHistories = [
      ...stockHistoriesWithoutProduced,
      ...unsyncedStockHistories,
    ];

    const repeatTime = Math.max(
      combinedProductionDatas.length,
      stockHistories.length
    );

    let orders = 0;
    for (let i = 0; i < repeatTime; i++) {
      let producedQty = 0;
      let produced: ProductionResponseType | null = null;
      if (combinedProductionDatas.length > i) {
        //생산품목이 실제 존재할 때
        produced = combinedProductionDatas[i];
        producedQty = combinedProductionDatas[i].productionCnt;
        orders++;
      } else {
        //생산품은 완료, 소모품이 완료되지 않아 생산 더미데이터가 같이 들어갈 때
        produced = combinedProductionDatas[combinedProductionDatas.length - 1];
        producedQty = 0;
      }

      let consumed: StockHistoryEssType | null = null;
      let consumedQty = 0;
      if (stockHistories.length > i) {
        consumed = stockHistories[i];
        consumedQty = convertNumberStringToNumber(consumed.changedQty);
      } else {
        consumed = stockHistories[stockHistories.length - 1];
      }

      const inbound: ProductionInboundDataType = {
        productionDt: produced.productionDt,
        orders: 1,
        factoryCd:
          workbayList.find((workbay) => workbay.id === selectedWorkbayId)
            ?.workbayCode || '100', //api수정필요
        receiveWarehouseCd: '100',
        empCd: loginUserName || '',
        producedCd: produced.item.PROD_CD,
        producedDes: produced.item.PROD_DES,
        producedQty: producedQty.toString(),
        consumedCd: consumed.prodCd,
        consumedDes: consumed.prodDes,
        consumedQty: consumedQty.toString(),
        groupNo: orders,
      };
      inboundArray.push(inbound);
    }
    excelDownloadHandler(inboundArray);
  }

  useEffect(() => {
    //헤더에 버튼 추가
    setButtons(
      <>
        <SquareButton
          text='엑셀출력'
          colorType={clickable ? 'ACTIVE' : 'DISACTIVE'}
          onClick={() => {
            clickable &&
              translateToExcel(
                stockHistories,
                filteredProductions,
                filteredUshList
              );
          }}
        />
      </>
    );
    // 페이지가 바뀌면 버튼 초기화
    return () => setButtons(null);
  }, [
    setButtons,
    stockHistories,
    clickable,
    stockHistoriesOrigin,
    filteredProductions,
    filteredUshList,
  ]);

  useEffect(() => {
    if (
      selectedWorkbayId === -1 ||
      unCompletedProductions.length > 0 ||
      filteredProductions.length < 1
    ) {
      setClickable(false);
    } else {
      setClickable(true);
    }
  }, [selectedWorkbayId, unCompletedProductions, filteredProductions]);

  function qtyChangeHandler(targetItemId: number, value: number | string) {
    setStockHistories((prev) =>
      prev.map((stockHistory) =>
        stockHistory.itemId === targetItemId
          ? {
            ...stockHistory,
            changedQty: value,
          }
          : stockHistory
      )
    );
  }

  function ushQtyChangeHandler(targetItemId: number, value: number) {
    setFilteredUshList((prev) =>
      prev.map((ush) =>
        ush.itemId === targetItemId
          ? {
            ...ush,
            changedQty: value,
          }
          : ush
      )
    );
  }

  // useEffect(() => {
  // const stockHistoryMap = new Map<number, StockHistoryResponseType>();

  // filteredProductions.forEach((production) => {
  //   const matchingHistories = stockHistories.filter(
  //     (stockHistory) => stockHistory.itemId === production.item.id
  //   );

  //   matchingHistories.forEach((stockHistory) => {
  //     if (stockHistoryMap.has(stockHistory.itemId)) {
  //       // If itemId already exists, sum the consumedQty
  //       const existingStockHistory = stockHistoryMap.get(
  //         stockHistory.itemId
  //       )!;
  //       existingStockHistory.changedQuantity += stockHistory.changedQuantity;
  //     } else {
  //       // Add new entry to the map
  //       stockHistoryMap.set(stockHistory.itemId, { ...stockHistory });
  //     }
  //   });
  // });

  // // Convert the map back to an array and set it in state
  // // setStockHistoryGroup(Array.from(stockHistoryMap.values()));
  // console.log(Array.from(stockHistoryMap.values()));
  // }, [filteredProductions]);

  return (
    <Container>
      <WorkbaySection>
        {workbayList.length > 0 &&
          workbayList
            //100은 엠마우스 본사코드로 사용안함
            .filter((workbay) => workbay.workbayCode !== '100')
            .map((workbay) => (
              <WorkbayItem
                selected={selectedWorkbayId === workbay.id}
                onClick={() =>
                  setSelectedWorkbayId((prev) =>
                    prev === workbay.id ? prev : workbay.id
                  )
                }
              >
                {workbay.workbayName}
              </WorkbayItem>
            ))}
      </WorkbaySection>
      {unCompletedProductions.length > 0 && (
        <h4 style={{ marginTop: '5px', color: 'red' }}>
          마감되지 않은 생산항목이 존재합니다. 먼저 생산진행에서 마감처리를
          해주세요.
        </h4>
      )}
      {selectedWorkbayId === -1 ? (
        <div>작업장을 선택해주세요</div>
      ) : (
        productionData.length > 0 && (
          <>
            <TableHeader>
              {formatEightDigitStringToSixDigitDot(nowDateString)} - 생산품
            </TableHeader>
            <Table>
              <th>
                <td className='prod-cell'>품목</td>
                <td className='achieve-cell'>달성수량</td>
                <td className='goal-cell'>목표수량</td>
                <td className='state-cell'>달성여부</td>
                <td className='achieve-ratio-cell'>달성률</td>
              </th>
              {filteredProductions.length > 0 &&
                filteredProductions.map((production) => {
                  const percentage =
                    (production.productionCnt / production.productionGoal) *
                    100;
                  return (
                    <tr>
                      <td className='prod-cell'>
                        {production.item.PROD_DES} / {production.item.SIZE_DES}
                      </td>
                      <td className='achieve-cell'>
                        {production.productionCnt}
                      </td>
                      <td className='goal-cell'>{production.productionGoal}</td>
                      <td className='state-cell'>
                        {production.progress === 'E'
                          ? '조기마감'
                          : percentage >= 100
                            ? '달성'
                            : '미달'}
                      </td>
                      <td className='achieve-ratio-cell'>
                        <CustomProgressBar
                          current={production.productionCnt}
                          goal={production.productionGoal}
                          style={{
                            width: '100%',
                            height: '8px',
                            borderRadius: '5px',
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
            </Table>
          </>
        )
      )}
      <hr />
      <TableHeader>
        {formatEightDigitStringToSixDigitDot(
          getYearMonthDateFromDate(new Date()).dateString
        )}{' '}
        - 소모품
      </TableHeader>
      <Table>
        <th>
          <td className='prod-cell'>품목명</td>
          <td className='achieve-cell'>등록된 소모수량</td>
          <td className='goal-cell'>소모수량 직접 적용</td>
        </th>
        {stockHistories.length > 0 &&
          stockHistories
            //생산품은 필터링
            .filter(
              (stockHistory) =>
                !productionData.find(
                  (production) => production.item.id === stockHistory.itemId
                )
            )

            .map((stockHistory) => {
              return (
                <tr>
                  <td className='prod-cell'>
                    {stockHistory.prodDes} / {stockHistory.sizeDes}
                  </td>
                  <td className='achieve-cell'>
                    {stockHistory.expectedChangedQty}
                  </td>
                  {/* <td className='goal-cell'>{stockHistory.changedQty}</td> */}
                  <input
                    value={stockHistory.changedQty}
                    className='goal-cell'
                    onChange={(e) => {
                      let value = e.target.value.trim();

                      // 숫자와 소수점만 허용하고, 소수점은 한 번만 입력 가능
                      value = value.replace(/[^0-9.]/g, ''); // 숫자와 소수점 외 문자 제거
                      if ((value.match(/\./g) || []).length > 1) {
                        value = value.replace(/\.(?=.*\.)/, ''); // 소수점이 2개 이상이면 첫 번째 소수점 이후 제거
                      }

                      // 소수점 2자리까지만 허용
                      const [integer, decimal] = value.split('.');
                      if (decimal && decimal.length > 2) {
                        value = `${integer}.${decimal.slice(0, 2)}`; // 소수점 2자리까지 자르기
                      }

                      // 앞의 '0' 제거 (소수점이 포함되었을 경우 처리)
                      value = value.replace(/^0+(?=\d)/, '');

                      qtyChangeHandler(stockHistory.itemId, value);
                    }}
                    type='text'
                    style={{ textAlign: 'center' }}
                  />
                </tr>
              );
            })}
      </Table>
      <hr />
      <TableHeader>
        {formatEightDigitStringToSixDigitDot(
          getYearMonthDateFromDate(new Date()).dateString
        )}{' '}
        - 개별 소모품
      </TableHeader>
      <Table>
        <th>
          <td className='prod-cell'>품목명</td>
          <td className='achieve-cell'>등록된 소모수량</td>
          <td className='goal-cell'>소모수량 직접 적용</td>
        </th>
        {filteredUshList.length > 0 &&
          filteredUshList.map((ush) => {
            return (
              <tr>
                <td className='prod-cell'>
                  {ush.prodDes} / {ush.sizeDes}
                </td>
                <td className='achieve-cell'>{ush.expectedChangedQty}</td>
                {/* <td className='goal-cell'>{ush.changedQty}</td> */}
                <input
                  value={ush.changedQty}
                  className='goal-cell'
                  onChange={(e) => {
                    let value = e.target.value.trim();
                    // 숫자만 남도록 필터링
                    value = value.replace(/[^0-9]/g, ''); // 숫자 외 문자 제거

                    // 앞의 '0' 제거
                    value = value.replace(/^0+/, '');

                    // 빈 값이면 0으로 설정

                    ushQtyChangeHandler(ush.itemId, Number(value));
                  }}
                  type='text'
                  style={{ textAlign: 'center' }}
                />
              </tr>
            );
          })}
      </Table>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  * {
    box-sizing: border-box;
    font-weight: 600;
  }
`;

const WorkbaySection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  width: auto;
  margin-bottom: 15px;
  height: 33px;
`;

const WorkbayItem = styled.div<{ selected: boolean }>`
  background-color: ${({ theme }) => theme.colors.white};
  padding: 3px 7px;
  border-radius: 4px;
  box-sizing: border-box;
  border: 3px solid;
  border-color: ${({ theme, selected }) =>
    selected ? theme.colors.grapeFruit : 'transparent'};
  color: ${({ theme }) => theme.colors.charcoal};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontsize.header_semi};
  cursor: pointer;
`;

const TableHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.charcoal};
  font-size: ${({ theme }) => theme.fontsize[21]};
  padding: 7px 25px;
  font-weight: 700;
`;

const Table = styled.table`
  width: 100%;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.white};
  .prod-cell {
    flex-basis: 30%;
  }
  .achieve-cell {
    flex-basis: 15%;
  }
  .goal-cell {
    flex-basis: 15%;
  }
  .state-cell {
    flex-basis: 15%;
  }
  .achieve-ratio-cell {
    flex-basis: 20%;
  }

  .consumed-prod-cell {
    flex: 1 0 30%;
  }
  .consumed-size-cell {
    flex: 1 0 15%;
  }
  .consumed-qty-cell {
    flex: 1 0 15%;
  }

  td {
    align-content: center;
    text-align: center;
  }
  th {
    display: flex;
    flex-direction: row;
  }
  tr {
    height: 30px;
    display: flex;
    flex-direction: row;
    align-items: center;
    td {
      overflow: hidden; /* 넘치는 부분 숨김 */
      text-overflow: ellipsis; /* 가려지는 부분 ... 처리 */
      white-space: nowrap; /* 줄 넘김 금지 */
    }
  }
`;
